// frontend/src/features/simulationConfiguration/components/questionsTable/QuestionsTable.tsx

import React, { useMemo } from 'react';
import { Edit } from 'lucide-react';
import DataSelect from '@/components/common/select/DataSelect';
import styles from '../../SimulationConfiguration.module.css';
import type { question, thematicArea, competence } from '../../types.api';

export interface questionsTableProps {
    questions: question[];
    thematicAreas: thematicArea[];
    competences: competence[];
    errors: Record<number, any>;
    onUpdate: (updatedQuestion: question) => void;
    page: number;
    pageSize: number;
    simulationType: string;
    onEditContent: (q: question) => void;
}

const QuestionsTable: React.FC<questionsTableProps> = ({
    questions,
    thematicAreas,
    competences,
    errors,
    onUpdate,
    page,
    pageSize,
    simulationType,
    onEditContent,
}) => {
    const isVirtualSimulation = simulationType === 'virtual';

    const thematicAreaOptions = useMemo(() =>
        thematicAreas.map(area => ({ value: String(area.id), label: area.name })),
        [thematicAreas]
    );

    const competenceOptions = useMemo(() =>
        competences.map(comp => ({ value: String(comp.id), label: comp.name })),
        [competences]
    );

    const DIFFICULTY_OPTIONS =[
        { value: 'easy', label: 'Fácil' },
        { value: 'medium', label: 'Medio' },
        { value: 'hard', label: 'Difícil' },
    ];

    const ANSWER_OPTIONS =[
        { value: 'A', label: 'A' }, { value: 'B', label: 'B' }, { value: 'C', label: 'C' },
        { value: 'D', label: 'D' }, { value: 'E', label: 'E' }, { value: 'F', label: 'F' },
        { value: 'G', label: 'G' },
    ];

    const getRelationValue = (field: question['thematic_area'] | question['competence']): string => {
        if (!field) return '';
        if (typeof field === 'object' && field !== null) {
            return String(field.id);
        }
        return String(field);
    };

    return (
        <div className={styles["table-responsive"]}>
            <table className={styles["table"]}>
                <thead>
                    <tr>
                        <th># Pregunta</th>
                        <th>Dificultad</th>
                        <th>Área temática</th>
                        <th>Competencia</th>
                        <th>Respuesta correcta</th>
                        {isVirtualSimulation && <th>Acciones</th>}
                    </tr>
                </thead>
                <tbody>
                    {questions.map((q, index) => {
                        // El index sigue sirviendo para los errores de validación
                        const absoluteIndex = (page - 1) * pageSize + index;
                        const questionErrors = errors[absoluteIndex] || {};

                        return (
                            <tr key={q.id || index}>
                                <td data-label="# Pregunta">{q.content?.session_question_number}</td>
                                <td data-label="Dificultad">
                                    <DataSelect
                                        name={`difficulty-${q.id}`}
                                        value={q.difficulty}
                                        onChange={(e) => onUpdate({ ...q, difficulty: e.target.value as question['difficulty'] })}
                                        error={questionErrors.difficulty}
                                        options={DIFFICULTY_OPTIONS}
                                        placeholder="Seleccionar..."
                                    />
                                </td>
                                <td data-label="Área temática">
                                    <DataSelect
                                        name={`thematic_area-${q.id}`}
                                        value={getRelationValue(q.thematic_area)}
                                        onChange={(e) => {
                                            const value = e.target.value ? Number(e.target.value) : null;
                                            onUpdate({ ...q, thematic_area: value as any });
                                        }}
                                        error={questionErrors.thematic_area}
                                        options={thematicAreaOptions}
                                        placeholder="Seleccionar..."
                                    />
                                </td>
                                <td data-label="Competencia">
                                    <DataSelect
                                        name={`competence-${q.id}`}
                                        value={getRelationValue(q.competence)}
                                        onChange={(e) => {
                                            const value = e.target.value ? Number(e.target.value) : null;
                                            onUpdate({ ...q, competence: value as any });
                                        }}
                                        error={questionErrors.competence}
                                        options={competenceOptions}
                                        placeholder="Seleccionar..."
                                    />
                                </td>
                                <td data-label="Respuesta correcta">
                                    <DataSelect
                                        name={`correct_answer-${q.id}`}
                                        value={q.correct_answer}
                                        onChange={(e) => onUpdate({ ...q, correct_answer: e.target.value as question['correct_answer'] })}
                                        error={questionErrors.correct_answer}
                                        options={ANSWER_OPTIONS}
                                        placeholder="..."
                                    />
                                </td>
                                {isVirtualSimulation && (
                                    <td data-label="Acciones">
                                        <button onClick={() => onEditContent(q)} className={styles.actionButton}>
                                            <Edit size={16} />
                                            <span>Editar contenido</span>
                                        </button>
                                    </td>
                                )}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

export default QuestionsTable;